import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Table,
  ActionIcon,
  TextInput,
  Select,
  NumberInput,
  Tabs,
  Badge,
  Alert,
  Loader,
  Paper,
  Tooltip,
} from '@mantine/core';
import {
  IconTrash,
  IconPlus,
  IconCalendar,
  IconAlertCircle,
  IconCheck,
  IconRefresh,
  IconSettings,
} from '@tabler/icons-react';

interface CustomHoliday {
  id: string;
  date: string;
  name: string;
  market: 'canada' | 'usa' | 'both';
  type: 'statutory' | 'custom' | 'blackout';
}

interface HolidaySettingsData {
  warningDays: number;
  customHolidays: CustomHoliday[];
}

interface BuiltinHoliday {
  date: string;
  name: string;
}

interface BuiltinHolidays {
  canada: BuiltinHoliday[];
  usa: BuiltinHoliday[];
}

interface Props {
  opened: boolean;
  onClose: () => void;
}

export function HolidaySettings({ opened, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<HolidaySettingsData>({
    warningDays: 3,
    customHolidays: [],
  });
  const [builtinHolidays, setBuiltinHolidays] = useState<BuiltinHolidays | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state for new holiday
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    name: '',
    market: 'both' as 'canada' | 'usa' | 'both',
    type: 'blackout' as 'statutory' | 'custom' | 'blackout',
  });

  const apiUrl = import.meta.env.VITE_API_URL ?? '';

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${apiUrl}/api/settings/holidays`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchBuiltinHolidays = useCallback(async () => {
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/holidays/builtin`);
      if (!response.ok) throw new Error('Failed to fetch builtin holidays');
      const data = await response.json();
      setBuiltinHolidays(data);
    } catch (err) {
      console.error('Failed to fetch builtin holidays:', err);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (opened) {
      fetchSettings();
      fetchBuiltinHolidays();
    }
  }, [opened, fetchSettings, fetchBuiltinHolidays]);

  const showMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  };

  const refreshCache = async () => {
    try {
      await apiFetch(`${apiUrl}/api/settings/holidays/refresh-cache`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to refresh cache:', err);
    }
  };

  const handleUpdateWarningDays = async (days: number) => {
    try {
      setSaving(true);
      const response = await apiFetch(`${apiUrl}/api/settings/holidays/warning-days`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warningDays: days }),
      });
      if (!response.ok) throw new Error('Failed to update warning days');
      setSettings((prev) => ({ ...prev, warningDays: days }));
      showMessage('success', 'Warning days updated');
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name) {
      showMessage('error', 'Date and name are required');
      return;
    }

    try {
      setSaving(true);
      const response = await apiFetch(`${apiUrl}/api/settings/holidays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newHoliday),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add holiday');
      }
      const data = await response.json();
      setSettings((prev) => ({
        ...prev,
        customHolidays: [...prev.customHolidays, data.holiday],
      }));
      setNewHoliday({ date: '', name: '', market: 'both', type: 'blackout' });
      await refreshCache();
      showMessage('success', 'Holiday added');
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : 'Failed to add holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (id: string) => {
    try {
      setSaving(true);
      const response = await apiFetch(`${apiUrl}/api/settings/holidays/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete holiday');
      setSettings((prev) => ({
        ...prev,
        customHolidays: prev.customHolidays.filter((h) => h.id !== id),
      }));
      await refreshCache();
      showMessage('success', 'Holiday deleted');
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : 'Failed to delete holiday');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getMarketBadge = (market: string) => {
    switch (market) {
      case 'canada':
        return <Badge color="red" size="sm">CA</Badge>;
      case 'usa':
        return <Badge color="blue" size="sm">US</Badge>;
      case 'both':
        return <Badge color="gray" size="sm">Both</Badge>;
      default:
        return null;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'statutory':
        return <Badge color="violet" size="sm">Statutory</Badge>;
      case 'custom':
        return <Badge color="teal" size="sm">Custom</Badge>;
      case 'blackout':
        return <Badge color="orange" size="sm">Blackout</Badge>;
      default:
        return null;
    }
  };

  // Filter to show only current year and future
  const currentYear = new Date().getFullYear();
  const isCurrentOrFutureYear = (dateStr: string) => {
    const year = parseInt(dateStr.substring(0, 4), 10);
    return year >= currentYear;
  };

  if (loading && opened) {
    return (
      <Modal opened={opened} onClose={onClose} title="Holiday Settings" size="lg">
        <Stack align="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading settings...</Text>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Holiday & Blackout Settings" size="xl">
      <Stack gap="lg">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" withCloseButton onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Warning Days Setting */}
        <Paper withBorder p="md">
          <Group justify="space-between" align="flex-end">
            <div>
              <Text fw={500}>Warning Days</Text>
              <Text size="sm" c="dimmed">
                Show holiday warnings this many days before a deadline falls on a non-business day
              </Text>
            </div>
            <NumberInput
              value={settings.warningDays}
              onChange={(val) => handleUpdateWarningDays(Number(val) || 0)}
              min={0}
              max={14}
              w={100}
              disabled={saving}
            />
          </Group>
        </Paper>

        <Tabs defaultValue="custom">
          <Tabs.List>
            <Tabs.Tab value="custom" leftSection={<IconCalendar size={16} />}>
              Custom Holidays ({settings.customHolidays.length})
            </Tabs.Tab>
            <Tabs.Tab value="builtin" leftSection={<IconSettings size={16} />}>
              Built-in Holidays
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="custom" pt="md">
            <Stack gap="md">
              {/* Add New Holiday Form */}
              <Paper withBorder p="md">
                <Text fw={500} mb="sm">Add Holiday / Blackout Date</Text>
                <Group align="flex-end" gap="sm">
                  <TextInput
                    label="Date"
                    type="date"
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label="Name"
                    placeholder="e.g., Company Closure"
                    value={newHoliday.name}
                    onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                    style={{ flex: 2 }}
                  />
                  <Select
                    label="Market"
                    data={[
                      { value: 'both', label: 'Both' },
                      { value: 'canada', label: 'Canada' },
                      { value: 'usa', label: 'USA' },
                    ]}
                    value={newHoliday.market}
                    onChange={(val) => setNewHoliday({ ...newHoliday, market: val as 'canada' | 'usa' | 'both' })}
                    style={{ width: 120 }}
                  />
                  <Select
                    label="Type"
                    data={[
                      { value: 'blackout', label: 'Blackout' },
                      { value: 'custom', label: 'Custom Holiday' },
                      { value: 'statutory', label: 'Statutory' },
                    ]}
                    value={newHoliday.type}
                    onChange={(val) => setNewHoliday({ ...newHoliday, type: val as 'statutory' | 'custom' | 'blackout' })}
                    style={{ width: 140 }}
                  />
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddHoliday}
                    loading={saving}
                  >
                    Add
                  </Button>
                </Group>
              </Paper>

              {/* Custom Holidays List */}
              {settings.customHolidays.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No custom holidays or blackout dates configured
                </Text>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Market</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th w={50}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {settings.customHolidays
                      .filter((h) => isCurrentOrFutureYear(h.date))
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((holiday) => (
                        <Table.Tr key={holiday.id}>
                          <Table.Td>{formatDate(holiday.date)}</Table.Td>
                          <Table.Td>{holiday.name}</Table.Td>
                          <Table.Td>{getMarketBadge(holiday.market)}</Table.Td>
                          <Table.Td>{getTypeBadge(holiday.type)}</Table.Td>
                          <Table.Td>
                            <Tooltip label="Delete">
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                onClick={() => handleDeleteHoliday(holiday.id)}
                                disabled={saving}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="builtin" pt="md">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                These are the built-in statutory holidays used for deadline calculations. They cannot be edited.
              </Text>

              <Tabs defaultValue="canada-builtin">
                <Tabs.List>
                  <Tabs.Tab value="canada-builtin">
                    Canada (Ontario)
                  </Tabs.Tab>
                  <Tabs.Tab value="usa-builtin">
                    USA (Federal)
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="canada-builtin" pt="sm">
                  {builtinHolidays?.canada ? (
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Name</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {builtinHolidays.canada
                          .filter((h) => isCurrentOrFutureYear(h.date))
                          .map((h) => (
                            <Table.Tr key={h.date}>
                              <Table.Td>{formatDate(h.date)}</Table.Td>
                              <Table.Td>{h.name}</Table.Td>
                            </Table.Tr>
                          ))}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    <Text c="dimmed">Loading...</Text>
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="usa-builtin" pt="sm">
                  {builtinHolidays?.usa ? (
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Name</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {builtinHolidays.usa
                          .filter((h) => isCurrentOrFutureYear(h.date))
                          .map((h) => (
                            <Table.Tr key={h.date}>
                              <Table.Td>{formatDate(h.date)}</Table.Td>
                              <Table.Td>{h.name}</Table.Td>
                            </Table.Tr>
                          ))}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    <Text c="dimmed">Loading...</Text>
                  )}
                </Tabs.Panel>
              </Tabs>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={async () => {
              await refreshCache();
              showMessage('success', 'Cache refreshed');
            }}
          >
            Refresh Cache
          </Button>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
